/**
 * Packaging OS — minimal API client (Phase 1N-B / 1P-A).
 *
 * Thin fetch wrapper around the live FastAPI backend. Properties:
 *   - Base URL configurable via VITE_PACKAGING_API_BASE_URL env var.
 *     Defaults to "" so `/api/whoami` resolves through Vite's dev-server
 *     proxy in development and through Caddy reverse-proxy in production.
 *   - Returns a discriminated union (`ApiResult`) instead of throwing on
 *     401/403/429/503/network — every non-success path becomes a typed
 *     branch the UI handles deliberately.
 *   - Surfaces the X-Request-Id response header for support / debug.
 *   - NEVER prints, stores, or logs the Authorization header / token /
 *     cookie.
 *   - NEVER stores secrets in localStorage / sessionStorage.
 *
 * This file intentionally does NOT implement an auth state machine.
 * Cookie-based auth is the only model — the browser sends the
 * `core_session` cookie automatically when `credentials: "include"`.
 */

const DEFAULT_TIMEOUT_MS = 8_000;

// Resolve API base URL.  Empty string → same-origin (dev proxy / prod Caddy).
const RAW_BASE = import.meta.env.VITE_PACKAGING_API_BASE_URL ?? "";
export const API_BASE = RAW_BASE.replace(/\/+$/, "");

// ---------------------------------------------------------------------------
// Result type — every API call returns one of these shapes.
// ---------------------------------------------------------------------------

export type ApiOutcome =
  | "ok"
  | "auth-missing"
  | "auth-invalid"
  | "permission-denied"
  | "rate-limited"
  | "validation-error"
  | "service-unavailable"
  | "network-error"
  | "unknown-error";

export interface ApiResult<T> {
  outcome: ApiOutcome;
  status: number;
  data: T | null;
  errorCode: string | null;
  errorMessage: string | null;
  requestId: string | null;
  retryAfterSeconds: number | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function classifyOutcome(status: number, code: string | null): ApiOutcome {
  if (status >= 200 && status < 300) return "ok";
  if (status === 401) {
    if (code === "packaging.api.auth_missing") return "auth-missing";
    if (code === "packaging.api.auth_invalid") return "auth-invalid";
    if (code === "packaging.api.auth_upstream_error") return "service-unavailable";
    return "auth-invalid";
  }
  if (status === 403) return "permission-denied";
  if (status === 422) return "validation-error";
  if (status === 429) return "rate-limited";
  if (status === 503) return "service-unavailable";
  if (status >= 500) return "service-unavailable";
  return "unknown-error";
}

function parseRetryAfter(headers: Headers): number | null {
  const v = headers.get("retry-after");
  if (!v) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// ---------------------------------------------------------------------------
// Core call
// ---------------------------------------------------------------------------

async function callApi<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { timeoutMs?: number },
): Promise<ApiResult<T>> {
  const url = `${API_BASE}${path}`;
  const controller = new AbortController();
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    // MM6: FormData passes through untouched — the browser sets the
    // multipart boundary; forcing a Content-Type would corrupt uploads.
    const isForm = typeof FormData !== "undefined" && body instanceof FormData;
    const response = await fetch(url, {
      method,
      headers: {
        Accept: "application/json",
        ...(body !== undefined && !isForm
          ? { "Content-Type": "application/json" }
          : {}),
      },
      // Cookie-based auth: browser handles `core_session` automatically.
      // We never read or write the cookie value from JS.
      credentials: "include",
      body:
        body !== undefined
          ? isForm
            ? (body as FormData)
            : JSON.stringify(body)
          : undefined,
      signal: controller.signal,
    });

    const requestId = response.headers.get("x-request-id");
    const retryAfterSeconds = parseRetryAfter(response.headers);

    let parsed: unknown = null;
    let errorCode: string | null = null;
    let errorMessage: string | null = null;
    try {
      parsed = await response.json();
    } catch {
      // Empty / non-JSON body — leave parsed null.
    }

    if (response.status >= 200 && response.status < 300) {
      return {
        outcome: "ok",
        status: response.status,
        data: parsed as T,
        errorCode: null,
        errorMessage: null,
        requestId,
        retryAfterSeconds,
      };
    }

    // FastAPI controlled error envelopes: { detail: { error, code, detail } }
    if (
      parsed &&
      typeof parsed === "object" &&
      "detail" in parsed &&
      typeof (parsed as Record<string, unknown>).detail === "object"
    ) {
      const detail = (parsed as { detail: Record<string, unknown> }).detail;
      errorCode = typeof detail.code === "string" ? detail.code : null;
      errorMessage = typeof detail.detail === "string" ? detail.detail : null;
    }

    return {
      outcome: classifyOutcome(response.status, errorCode),
      status: response.status,
      data: null,
      errorCode,
      errorMessage,
      requestId,
      retryAfterSeconds,
    };
  } catch (err) {
    const isAbort = (err as { name?: string })?.name === "AbortError";
    return {
      outcome: "network-error",
      status: 0,
      data: null,
      errorCode: isAbort ? "client.timeout" : "client.network_error",
      errorMessage: isAbort
        ? "request timed out"
        : "could not reach the Packaging OS API",
      requestId: null,
      retryAfterSeconds: null,
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

// ---------------------------------------------------------------------------
// Typed endpoints (Phase 1N-B + 1O-B / 1P-B)
// ---------------------------------------------------------------------------

export interface WhoAmI {
  app_code: string;
  user_id: string;
  email: string;
  access_mode: string;
  is_preview: boolean;
  role_codes: string[];
  permissions_count: number;
}

export function getWhoAmI(): Promise<ApiResult<WhoAmI>> {
  return callApi<WhoAmI>("GET", "/api/whoami");
}

// Workspace identity — admin-gated (Phase 1D-B).
export interface WorkspaceIdentity {
  app_code: string;
  app_id: string;
  app_slug: string;
  workspace_id: string;
  workspace_slug: string;
  organization_id: string;
  organization_slug: string;
  project_name: string;
  default_locale: string;
  supported_locales: string[];
  rtl_locales: string[];
  user_access_summary: {
    user_id: string;
    email: string;
    access_mode: string;
    is_preview: boolean;
    is_packaging_admin: boolean;
    is_super_admin: boolean;
    role_count: number;
  };
}

export function getWorkspaceIdentity(): Promise<ApiResult<WorkspaceIdentity>> {
  return callApi<WorkspaceIdentity>("GET", "/api/workspace/identity");
}

// ---------- Catalog list shapes (Phase 1E / 1G projection) ----------

export interface PaginatedList<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
  has_next: boolean;
}

export interface BrandRow {
  brand_id: string;
  organization_id: string;
  workspace_id: string;
  slug: string;
  name: string;
  legal_name: string | null;
  description_locales: Record<string, string>;
  tone_of_voice_locales: Record<string, string>;
  slogan_locales: Record<string, string>;
  color_palette: Record<string, unknown>;
  typography_directions: Record<string, unknown>;
  positioning_tier: string | null;
  reference_brands: unknown[];
  nonacceptable_brands: unknown[];
  version: number;
  is_active_version: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductRow {
  product_id: string;
  brand_id: string;
  organization_id: string;
  workspace_id: string;
  slug: string;
  name: string;
  category: string;
  subcategory: string | null;
  description_locales: Record<string, string>;
  weight_grams: number | null;
  volume_ml: number | null;
  dimensions_mm: Record<string, unknown> | null;
  ingredients: unknown[];
  features: unknown[];
  usage: Record<string, unknown>;
  target_price_band: Record<string, unknown> | null;
  target_markets: string[];
  sales_channels: string[];
  sensitivity_flags: Record<string, unknown>;
  version: number;
  is_active_version: boolean;
  created_at: string;
  updated_at: string;
}

export interface PackagingProjectRow {
  project_id: string;
  organization_id: string;
  workspace_id: string;
  slug: string;
  name: string;
  description_locales: Record<string, string>;
  subject_kind: string;
  subject_ref: string;
  target_markets: string[];
  target_languages: string[];
  sales_channels: string[];
  export_destinations: string[];
  regulatory_scope: string[];
  cultural_profile_tags: string[];
  supplier_region_preferences: string[];
  status: string;
  version: number;
  is_active_version: boolean;
  created_at: string;
  updated_at: string;
}

interface ListParams {
  page?: number;
  pageSize?: number;
}

function paginatedQuery(params?: ListParams): string {
  const search = new URLSearchParams();
  if (params?.page) search.set("page", String(params.page));
  if (params?.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function getBrands(
  params?: ListParams,
): Promise<ApiResult<PaginatedList<BrandRow>>> {
  return callApi<PaginatedList<BrandRow>>(
    "GET",
    `/api/brands${paginatedQuery(params)}`,
  );
}

export function getProducts(
  params?: ListParams,
): Promise<ApiResult<PaginatedList<ProductRow>>> {
  return callApi<PaginatedList<ProductRow>>(
    "GET",
    `/api/products${paginatedQuery(params)}`,
  );
}

export function getPackagingProjects(
  params?: ListParams,
): Promise<ApiResult<PaginatedList<PackagingProjectRow>>> {
  return callApi<PaginatedList<PackagingProjectRow>>(
    "GET",
    `/api/packaging-projects${paginatedQuery(params)}`,
  );
}

// ---------- Dry-run packaging audit (Phase 1H/1I contract) ----------

export const DRY_RUN_CHANNELS = [
  "retail",
  "ecommerce",
  "restaurant",
  "delivery",
  "export",
  "other",
] as const;
export const DRY_RUN_PACKAGE_TYPES = [
  "box",
  "pouch",
  "bottle",
  "jar",
  "cup",
  "bag",
  "wrapper",
  "other",
] as const;
export const DRY_RUN_LANGUAGES = ["en", "ar", "fa", "tr"] as const;

export type DryRunChannel = (typeof DRY_RUN_CHANNELS)[number];
export type DryRunPackageType = (typeof DRY_RUN_PACKAGE_TYPES)[number];
export type DryRunLanguage = (typeof DRY_RUN_LANGUAGES)[number];

export interface DryRunRequest {
  product_name: string;
  product_category: string;
  market: string;
  channel?: DryRunChannel | null;
  package_type?: DryRunPackageType | null;
  material_preference?: string | null;
  claims?: string[];
  target_customer?: string | null;
  language?: DryRunLanguage;
  notes?: string | null;
}

export interface DryRunInputSummary {
  product_name: string;
  product_category: string;
  market: string;
  channel: string | null;
  package_type: string | null;
  material_preference: string | null;
  claims_count: number;
  language: string;
}

export interface DryRunPackagingAudit {
  positioning_notes: string[];
  pack_structure_notes: string[];
  shelf_and_delivery_considerations: string[];
}

export interface DryRunLabelCopy {
  front_of_pack: string[];
  side_panel: string[];
  tone_notes: string[];
}

export interface DryRunClaimRisk {
  risk_level: "low" | "medium" | "high";
  flags: string[];
  review_required: boolean;
  review_reasons: string[];
}

export interface DryRunTrustNotes {
  source_backed: boolean;
  human_review_required: boolean;
  regulatory_guarantee: boolean;
  final_output: boolean;
}

// Phase Core/AI: shape of the AI-assist overlay returned by the
// backend on every workflow response. The deterministic body is
// authoritative; this overlay is advisory only and the human-review
// banner is preserved on every response.
export type AIAssistStatus = "off" | "ok" | "fallback";

export interface AIAuditEnvelope {
  task_type: string;
  prompt_handle: string;
  prompt_hash_short: string;
  status: string;
  latency_ms: number;
  cost_usd: number;
  fallback_reason: string | null;
}

// Optional AI overlay fields the backend may merge into a workflow
// response. None of these are required because the deterministic body
// is the source of truth.
export interface AIAssistOverlay {
  ai_assist_status?: AIAssistStatus;
  ai_assist_fallback_reason?: string;
  ai_audit?: AIAuditEnvelope | null;
  ai_brand_voice_summary?: string[];
  ai_claim_risk_summary?: string[];
  ai_front_label_alts?: string[];
  ai_back_label_alts?: string[];
  ai_safer_wording_alts?: string[];
  ai_risk_annotations?: string[];
  ai_substitute_questions?: string[];
  ai_greenwashing_notes?: string[];
  ai_substantiation_questions?: string[];
  ai_tone_suggestions?: string[];
  ai_phrasing_alts?: string[];
  ai_moodboard_phrases?: string[];
  ai_designer_questions?: string[];
  ai_score_rationale?: string[];
  ai_improvement_questions?: string[];
  ai_section_variations?: Record<string, string[]>;
  no_send_notice?: string;
  no_image_generated?: boolean;
}

export interface DryRunResponse {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: DryRunInputSummary;
  packaging_audit: DryRunPackagingAudit;
  label_copy_suggestions: DryRunLabelCopy;
  material_notes: string[];
  claim_risk: DryRunClaimRisk;
  supplier_brief_outline: string[];
  launch_kit_outline: string[];
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runPackagingAuditDryRun(
  body: DryRunRequest,
): Promise<ApiResult<DryRunResponse>> {
  return callApi<DryRunResponse>(
    "POST",
    "/api/workflows/packaging-audit/dry-run",
    body,
  );
}

// ---------- Phase 22A — Product Brief + Brand Intake ----------

export const PRODUCT_FORMS = [
  "solid",
  "liquid",
  "powder",
  "gel",
  "paste",
  "granular",
  "gas",
  "composite",
  "other",
] as const;
export const SIZE_UNITS = [
  "g",
  "kg",
  "ml",
  "l",
  "oz",
  "count",
  "pack",
  "set",
  "other",
] as const;
export const PRICE_BANDS = [
  "value",
  "mainstream",
  "premium",
  "super_premium",
  "unknown",
] as const;
export const BRAND_MATURITY = [
  "pre_launch",
  "launching",
  "early_stage",
  "established",
  "legacy",
] as const;
export const PACKAGING_STATUS = [
  "none",
  "prototype",
  "existing_in_market",
  "redesign_in_progress",
] as const;
export const INTAKE_OBJECTIVES = [
  "new_packaging",
  "redesign",
  "export_adaptation",
  "premiumization",
  "cost_reduction",
  "compliance_review",
  "ecommerce_readiness",
] as const;
export const BUDGET_BANDS = ["low", "mid", "high", "unknown"] as const;
export const ASSETS_AVAILABLE = [
  "product_photos",
  "current_label_text",
  "brand_guideline",
  "competitor_references",
  "supplier_specs",
  "none",
] as const;

export type ProductForm = (typeof PRODUCT_FORMS)[number];
export type SizeUnit = (typeof SIZE_UNITS)[number];
export type PriceBand = (typeof PRICE_BANDS)[number];
export type BrandMaturity = (typeof BRAND_MATURITY)[number];
export type PackagingStatus = (typeof PACKAGING_STATUS)[number];
export type IntakeObjective = (typeof INTAKE_OBJECTIVES)[number];
export type BudgetBand = (typeof BUDGET_BANDS)[number];
export type AssetAvailable = (typeof ASSETS_AVAILABLE)[number];

export interface ProductBrandIntakeRequest {
  product_name: string;
  product_category: string;
  product_subcategory?: string | null;
  product_description?: string | null;
  product_form: ProductForm;
  size_value?: number | null;
  size_unit?: SizeUnit | null;
  price_band: PriceBand;
  brand_name: string;
  brand_personality?: string | null;
  brand_maturity: BrandMaturity;
  current_packaging_status: PackagingStatus;
  visual_preferences?: string | null;
  tone_of_voice?: string | null;
  target_markets: string[];
  target_languages: DryRunLanguage[];
  sales_channels: DryRunChannel[];
  target_audience?: string | null;
  objectives: IntakeObjective[];
  material_constraints?: string | null;
  budget_band: BudgetBand;
  production_constraints?: string | null;
  sustainability_preferences: string[];
  claims_to_avoid: string[];
  assets_available: AssetAvailable[];
  language: DryRunLanguage;
  notes?: string | null;
  // Phase Core/AI: optional flag for AI-assist enrichment overlay.
  // Backend default is `false`; deterministic body is unchanged.
  ai_assist?: boolean;
}

export interface ProductBrandIntakeResponse extends AIAssistOverlay {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: Record<string, unknown>;
  product_brief_summary: string[];
  brand_context_summary: string[];
  packaging_objective_summary: string[];
  market_channel_fit_notes: string[];
  missing_information_checklist: string[];
  assumption_log: string[];
  initial_risk_flags: string[];
  human_review_required_notes: string[];
  recommended_next_engines: string[];
  // Phase 22B deepening sections (optional for backwards compat)
  packaging_strategy_snapshot?: string[];
  target_customer_interpretation?: string[];
  positioning_notes?: string[];
  channel_readiness_notes?: string[];
  language_localization_notes?: string[];
  packaging_decision_priorities?: string[];
  owner_questions_to_resolve?: string[];
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runProductBrandIntakeDryRun(
  body: ProductBrandIntakeRequest,
): Promise<ApiResult<ProductBrandIntakeResponse>> {
  return callApi<ProductBrandIntakeResponse>(
    "POST",
    "/api/workflows/product-brief/dry-run",
    body,
  );
}

// ---------- Phase 23A — Label / Copy + Claim Risk ----------

export interface LabelCopyDryRunRequest {
  product_name: string;
  category: string;
  brand_name: string;
  target_market: string;
  target_language: DryRunLanguage;
  sales_channel?: DryRunChannel | null;
  product_description?: string | null;
  packaging_objective: IntakeObjective;
  claim_drafts: string[];
  tone_of_voice?: string | null;
  claims_to_avoid: string[];
  required_label_elements: string[];
  // Phase Core/AI: optional flag for AI-assist enrichment overlay.
  ai_assist?: boolean;
}

export interface ClaimRiskRow {
  claim: string;
  severity: "low" | "medium" | "high" | "review_required";
  category: string;
  matched_terms: string[];
  review_required: boolean;
}

export interface LabelCopyDryRunResponse extends AIAssistOverlay {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: Record<string, unknown>;
  front_label_draft: string[];
  back_label_draft: string[];
  short_product_story: string[];
  usage_instructions: string[];
  warning_caution_draft: string[];
  claim_risk_table: ClaimRiskRow[];
  safer_wording_suggestions: string[];
  missing_label_information: string[];
  human_review_required_notes: string[];
  copy_quality_notes: string[];
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runLabelCopyDryRun(
  body: LabelCopyDryRunRequest,
): Promise<ApiResult<LabelCopyDryRunResponse>> {
  return callApi<LabelCopyDryRunResponse>(
    "POST",
    "/api/workflows/label-copy/dry-run",
    body,
  );
}

// ---------- Phase 24 — Material / Structure ----------

export const FRAGILITY_LEVELS = ["none", "low", "medium", "high"] as const;
export type FragilityLevel = (typeof FRAGILITY_LEVELS)[number];

export interface MaterialStructureRequest {
  product_name: string;
  category: string;
  subcategory?: string | null;
  product_form: ProductForm;
  size_value?: number | null;
  size_unit?: SizeUnit | null;
  fragility: FragilityLevel;
  temperature_sensitive: boolean;
  target_market: string;
  sales_channel?: DryRunChannel | null;
  price_band: PriceBand;
  packaging_objective: IntakeObjective;
  sustainability_preferences: string[];
  budget_band: BudgetBand;
  production_constraints?: string | null;
  notes?: string | null;
  language: DryRunLanguage;
  // Phase Core/AI: optional flag for AI-assist enrichment overlay.
  ai_assist?: boolean;
}

export interface MaterialStructureResponse extends AIAssistOverlay {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: Record<string, unknown>;
  draft_only_disclaimer: string;
  recommended_packaging_format: string[];
  material_recommendation: string[];
  structure_recommendation: string[];
  durability_handling_notes: string[];
  delivery_suitability_notes: string[];
  ecommerce_suitability_notes: string[];
  shelf_suitability_notes: string[];
  production_feasibility_notes: string[];
  cost_sensitivity_notes: string[];
  open_questions_for_supplier: string[];
  human_review_required_notes: string[];
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runMaterialStructureDryRun(
  body: MaterialStructureRequest,
): Promise<ApiResult<MaterialStructureResponse>> {
  return callApi<MaterialStructureResponse>(
    "POST",
    "/api/workflows/material-structure/dry-run",
    body,
  );
}

// ---------- Phase 25A — Sustainability / Greenwashing / Compliance Risk ----------

export interface SustainabilityRiskRequest {
  product_name: string;
  category: string;
  target_market: string;
  target_language: DryRunLanguage;
  sales_channel?: DryRunChannel | null;
  packaging_material?: string | null;
  sustainability_claims: string[];
  label_copy_drafts: string[];
  has_recyclable_claim: boolean;
  has_biodegradable_claim: boolean;
  has_compostable_claim: boolean;
  has_origin_claim: boolean;
  barcode_present: boolean;
  expiry_present: boolean;
  origin_label_present: boolean;
  warning_present: boolean;
  notes?: string | null;
  // Phase Core/AI: optional flag for AI-assist enrichment overlay.
  ai_assist?: boolean;
}

export interface ClaimSeverityRow {
  term: string;
  severity: "low" | "medium" | "high" | "review_required";
  category: string;
  evidence_required: boolean;
}

export interface SustainabilityRiskResponse extends AIAssistOverlay {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: Record<string, unknown>;
  sustainability_claim_review: string[];
  greenwashing_risk_flags: string[];
  claim_severity_table: ClaimSeverityRow[];
  safer_sustainability_wording: string[];
  compliance_reminder_checklist: string[];
  country_review_notes: string[];
  required_evidence: string[];
  human_review_required_notes: string[];
  not_approved_notice: string;
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runSustainabilityRiskDryRun(
  body: SustainabilityRiskRequest,
): Promise<ApiResult<SustainabilityRiskResponse>> {
  return callApi<SustainabilityRiskResponse>(
    "POST",
    "/api/workflows/sustainability-risk/dry-run",
    body,
  );
}

// ---------- Phase 26 — Supplier / RFQ ----------

export const QUANTITY_RANGES = [
  "under_1k",
  "1k_5k",
  "5k_25k",
  "25k_100k",
  "over_100k",
  "unknown",
] as const;
export const PRINT_FINISHES = [
  "offset",
  "digital",
  "flexo",
  "screen",
  "hot_stamp_foil",
  "emboss_deboss",
  "matte_lamination",
  "gloss_lamination",
  "uv_spot",
  "none",
  "other",
] as const;
export type QuantityRange = (typeof QUANTITY_RANGES)[number];
export type PrintFinish = (typeof PRINT_FINISHES)[number];

export interface SupplierRfqRequest {
  product_name: string;
  category: string;
  brand_name: string;
  target_market: string;
  packaging_format: string;
  packaging_material: string;
  structure_recommendation?: string | null;
  size_value?: number | null;
  size_unit?: string | null;
  quantity_range: QuantityRange;
  price_band: PriceBand;
  print_finish: PrintFinish;
  sales_channel?: DryRunChannel | null;
  sustainability_preferences: string[];
  known_constraints?: string | null;
  questions_for_supplier: string[];
  notes?: string | null;
  language: DryRunLanguage;
  // Phase Core/AI: optional flag for AI-assist enrichment overlay.
  ai_assist?: boolean;
}

export interface SupplierRfqResponse extends AIAssistOverlay {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: Record<string, unknown>;
  supplier_brief_summary: string[];
  packaging_specification_draft: string[];
  material_structure_requirements: string[];
  print_finish_requirements: string[];
  quantity_questions: string[];
  lead_time_questions: string[];
  quality_testing_questions: string[];
  sustainability_evidence_questions: string[];
  rfq_email_draft: string[];
  supplier_comparison_criteria: string[];
  missing_production_information: string[];
  human_review_required_notes: string[];
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runSupplierRfqDryRun(
  body: SupplierRfqRequest,
): Promise<ApiResult<SupplierRfqResponse>> {
  return callApi<SupplierRfqResponse>(
    "POST",
    "/api/workflows/supplier-rfq/dry-run",
    body,
  );
}

// ---------- Phase 27A — Visual Direction ----------

export interface VisualDirectionRequest {
  product_name: string;
  category: string;
  brand_name: string;
  brand_personality?: string | null;
  target_market: string;
  target_customer?: string | null;
  packaging_objective: IntakeObjective;
  price_band: PriceBand;
  sales_channel?: DryRunChannel | null;
  visual_preference?: string | null;
  colors_to_use: string[];
  colors_to_avoid: string[];
  typography_preference?: string | null;
  competitor_references: string[];
  packaging_format?: string | null;
  packaging_material?: string | null;
  claims_to_avoid: string[];
  target_language: DryRunLanguage;
  notes?: string | null;
  // Phase Core/AI: optional flag for AI-assist enrichment overlay.
  ai_assist?: boolean;
}

export interface VisualDirectionResponse extends AIAssistOverlay {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: Record<string, unknown>;
  visual_direction_summary: string[];
  design_route_options: string[];
  color_direction: string[];
  typography_direction: string[];
  layout_direction: string[];
  packaging_surface_priority: string[];
  shelf_ecommerce_visual_notes: string[];
  cultural_localization_notes: string[];
  mockup_prompt_pack: string[];
  designer_handoff_notes: string[];
  visual_risks_avoidance_notes: string[];
  human_review_required_notes: string[];
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runVisualDirectionDryRun(
  body: VisualDirectionRequest,
): Promise<ApiResult<VisualDirectionResponse>> {
  return callApi<VisualDirectionResponse>(
    "POST",
    "/api/workflows/visual-direction/dry-run",
    body,
  );
}

// ---------- Phase 28 + 29A — Packaging Scorecard + Vertical Packs ----------

export const VERTICAL_PACKS = [
  "food_beverage",
  "restaurant_delivery",
  "export_gcc_tr_ir",
  "beauty_cosmetic",
  "ecommerce_d2c",
  "generic",
] as const;
export type VerticalPack = (typeof VERTICAL_PACKS)[number];

export const SHELF_ENVIRONMENTS = [
  "supermarket",
  "specialty",
  "convenience",
  "duty_free",
  "online_only",
  "other",
] as const;
export type ShelfEnvironment = (typeof SHELF_ENVIRONMENTS)[number];

export interface PackagingScorecardRequest {
  product_name: string;
  category: string;
  packaging_format: string;
  packaging_material: string;
  sales_channel?: DryRunChannel | null;
  target_market: string;
  price_band: PriceBand;
  weight_grams?: number | null;
  fragility: FragilityLevel;
  is_liquid: boolean;
  is_temperature_sensitive: boolean;
  visual_direction_notes?: string | null;
  ecommerce_thumbnail_optimized: boolean;
  has_protective_insert: boolean;
  has_leak_proof_seal: boolean;
  has_drop_test_passed: boolean;
  shelf_environment?: ShelfEnvironment | null;
  sustainability_claims: string[];
  vertical_pack: VerticalPack;
  notes?: string | null;
  language: DryRunLanguage;
  // Phase Core/AI: optional flag for AI-assist enrichment overlay.
  ai_assist?: boolean;
}

export interface ScorecardSection {
  label: string;
  score: number;
  raw_score: number;
  band: "low" | "medium" | "high";
  band_label: string;
  rationale: string[];
}

export interface PackagingScorecardResponse extends AIAssistOverlay {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: Record<string, unknown>;
  vertical_pack: {
    code: VerticalPack;
    label: string;
    lead: string;
    emphasis: string[];
    weights: Record<string, number>;
  };
  overall_score: number;
  overall_band: "low" | "medium" | "high";
  shelf_visibility_score: ScorecardSection;
  ecommerce_thumbnail_score: ScorecardSection;
  delivery_durability_score: ScorecardSection;
  unboxing_experience_score: ScorecardSection;
  marketplace_readiness_score: ScorecardSection;
  risk_flags: string[];
  improvement_suggestions: string[];
  channel_specific_recommendations: string[];
  missing_information: string[];
  human_review_required_notes: string[];
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runPackagingScorecardDryRun(
  body: PackagingScorecardRequest,
): Promise<ApiResult<PackagingScorecardResponse>> {
  return callApi<PackagingScorecardResponse>(
    "POST",
    "/api/workflows/packaging-scorecard/dry-run",
    body,
  );
}

// ---------- Phase 32 — Launch Kit ----------
export const LAUNCH_CHANNELS = [
  "instagram", "tiktok", "marketplace", "newsletter",
  "in_store", "influencer", "press", "other",
] as const;
export type LaunchChannel = (typeof LAUNCH_CHANNELS)[number];

export interface LaunchKitRequest {
  product_name: string;
  brand_name: string;
  category: string;
  target_market: string;
  target_language: DryRunLanguage;
  sales_channel?: DryRunChannel | null;
  packaging_positioning: string;
  key_benefit: string;
  claim_restrictions: string[];
  qr_connected: boolean;
  launch_channels: LaunchChannel[];
  notes?: string | null;
  // Phase Core/AI: optional flag for AI-assist enrichment overlay.
  ai_assist?: boolean;
}

export interface LaunchKitResponse extends AIAssistOverlay {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: Record<string, unknown>;
  launch_positioning_summary: string[];
  ecommerce_listing_draft: string[];
  social_content_drafts: string[];
  insert_card_copy: string[];
  qr_connected_packaging_idea: string[];
  review_reorder_loyalty_copy: string[];
  claim_safety_notes: string[];
  missing_information: string[];
  human_review_required_notes: string[];
  no_send_notice: string;
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runLaunchKitDryRun(
  body: LaunchKitRequest,
): Promise<ApiResult<LaunchKitResponse>> {
  return callApi<LaunchKitResponse>(
    "POST", "/api/workflows/launch-kit/dry-run", body,
  );
}

// ---------- Phase 33 — Post-Launch Analysis ----------
export interface PostLaunchAnalysisRequest {
  product_name: string;
  sales_channel?: DryRunChannel | null;
  review_snippets: string[];
  return_reasons: string[];
  customer_feedback_notes: string[];
  ecommerce_performance_notes?: string | null;
  packaging_version?: string | null;
  notes?: string | null;
  target_language: DryRunLanguage;
}

export interface PostLaunchAnalysisResponse {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: Record<string, unknown>;
  feedback_theme_summary: string[];
  packaging_complaint_classification: string[];
  return_damage_classification: string[];
  review_sentiment_notes: string[];
  packaging_improvement_suggestions: string[];
  next_iteration_priorities: string[];
  data_quality_notes: string[];
  human_review_required_notes: string[];
  no_external_notice: string;
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runPostLaunchAnalysisDryRun(
  body: PostLaunchAnalysisRequest,
): Promise<ApiResult<PostLaunchAnalysisResponse>> {
  return callApi<PostLaunchAnalysisResponse>(
    "POST", "/api/workflows/post-launch-analysis/dry-run", body,
  );
}

// ---------- Phase 34 — Review Checklist ----------
export const REVIEW_TARGETS = [
  "product-brief", "label-copy", "material-structure",
  "sustainability-risk", "supplier-rfq", "visual-direction",
  "packaging-scorecard", "launch-kit", "post-launch-analysis", "other",
] as const;
export type ReviewTarget = (typeof REVIEW_TARGETS)[number];

export interface ReviewChecklistRequest {
  workflow_target: ReviewTarget;
  product_name: string;
  target_market: string;
  has_high_risk_claim: boolean;
  has_evidence_gap: boolean;
  has_unsupported_sustainability_claim: boolean;
  has_food_contact_concern: boolean;
  has_cosmetic_safety_concern: boolean;
  has_industrial_hazard_concern: boolean;
  has_supplier_uncertainty: boolean;
  target_language: DryRunLanguage;
  notes?: string | null;
}

export interface ReviewChecklistResponse {
  dry_run: boolean;
  draft_only: boolean;
  workflow: string;
  input_summary: Record<string, unknown>;
  draft_only_disclaimer: string;
  review_checklist: string[];
  approval_criteria: string[];
  risk_review_items: string[];
  evidence_required: string[];
  export_readiness: string[];
  reviewer_decision_template: string[];
  no_approval_notice: string;
  trust_notes: DryRunTrustNotes;
  next_steps: string[];
}

export function runReviewChecklistDryRun(
  body: ReviewChecklistRequest,
): Promise<ApiResult<ReviewChecklistResponse>> {
  return callApi<ReviewChecklistResponse>(
    "POST", "/api/workflows/review-checklist/dry-run", body,
  );
}

// Public health probe — called from the public landing trust strip.
export interface HealthPayload {
  status: string;
  app: string;
  mode: string;
  ai_enabled: boolean;
  provider_keys_required: boolean;
  deployment_phase: string;
}

export function getHealth(): Promise<ApiResult<HealthPayload>> {
  return callApi<HealthPayload>("GET", "/health");
}

// ---------------------------------------------------------------------------
// MM2 — internal auth endpoints (Packaging OS-owned; no central core)
// ---------------------------------------------------------------------------

export interface AuthUser {
  user_id: string;
  email: string;
  display_name: string;
  status: string;
  default_locale: string;
}

export interface LoginResponse {
  ok: boolean;
  user: AuthUser;
  // The client never stores this value — cookie-based auth is the
  // browser flow. It exists for token-based callers (CLI/tests).
  session_token: string;
  expires_at: string;
}

export interface AuthMe {
  app_code: string;
  user_id: string;
  email: string;
  display_name: string;
  access_mode: string;
  is_preview: boolean;
  is_owner: boolean;
  role_codes: string[];
  permissions_count: number;
  workspace_id: string;
  organization_id: string;
  auth_source: string;
}

export function postLogin(
  email: string,
  password: string,
): Promise<ApiResult<LoginResponse>> {
  return callApi<LoginResponse>("POST", "/api/auth/login", { email, password });
}

export function postLogout(): Promise<ApiResult<{ ok: boolean; revoked: boolean }>> {
  return callApi("POST", "/api/auth/logout");
}

export function getAuthMe(): Promise<ApiResult<AuthMe>> {
  return callApi<AuthMe>("GET", "/api/auth/me");
}

export function postRequestAccess(
  email: string,
  message: string,
): Promise<ApiResult<{ ok: boolean; request_id: string; status: string }>> {
  return callApi("POST", "/api/auth/request-access", { email, message });
}

// ---------------------------------------------------------------------------
// MM3 — memory / artifact / evidence endpoints (backend foundation; the
// dedicated UI pages are deferred — these methods power upcoming panels)
// ---------------------------------------------------------------------------

export interface MemoryItem {
  id: string;
  key: string;
  category: string;
  truth_status: string;
  value_json: Record<string, unknown>;
  confidence: number | null;
  locked: boolean;
  evidence_id: string | null;
  resolved_at: string | null;
  created_at: string;
}

export interface ArtifactSummary {
  id: string;
  workspace_id: string;
  artifact_type: string;
  title: string;
  status: string;
  workflow_key: string | null;
  source_run_id: string | null;
  latest_version_no: number;
  created_at: string;
}

export interface SourceEvidence {
  id: string;
  source_type: string;
  source_name: string;
  source_url: string | null;
  reliability: string;
  verified: boolean;
  needs_review: boolean;
  jurisdiction: string | null;
  created_at: string;
}

export interface ItemsResponse<T> {
  items: T[];
  count: number;
}

export function getMemoryProject(params?: {
  category?: string;
  project_id?: string;
}): Promise<ApiResult<ItemsResponse<MemoryItem>>> {
  const qs = new URLSearchParams();
  if (params?.category) qs.set("category", params.category);
  if (params?.project_id) qs.set("project_id", params.project_id);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return callApi("GET", `/api/memory/project${suffix}`);
}

export function getMemoryAssumptions(): Promise<ApiResult<ItemsResponse<MemoryItem>>> {
  return callApi("GET", "/api/memory/assumptions");
}

export function getSourceGaps(): Promise<ApiResult<ItemsResponse<MemoryItem>>> {
  return callApi("GET", "/api/source-gaps");
}

export function getHumanReviewNeeds(): Promise<ApiResult<ItemsResponse<MemoryItem>>> {
  return callApi("GET", "/api/memory/human-review-needs");
}

export function getArtifacts(params?: {
  artifact_type?: string;
  status?: string;
}): Promise<ApiResult<ItemsResponse<ArtifactSummary>>> {
  const qs = new URLSearchParams();
  if (params?.artifact_type) qs.set("artifact_type", params.artifact_type);
  if (params?.status) qs.set("status", params.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return callApi("GET", `/api/artifacts${suffix}`);
}

export function getArtifact(id: string): Promise<ApiResult<{
  artifact: ArtifactSummary;
  comments: unknown[];
  reviews: unknown[];
  linked_sources: unknown[];
  linked_memory: unknown[];
}>> {
  return callApi("GET", `/api/artifacts/${encodeURIComponent(id)}`);
}

export function getArtifactVersions(
  id: string,
): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  return callApi("GET", `/api/artifacts/${encodeURIComponent(id)}/versions`);
}

export function getEvidence(): Promise<ApiResult<ItemsResponse<SourceEvidence>>> {
  return callApi("GET", "/api/evidence");
}

export function postEvidence(body: {
  source_type: string;
  source_name: string;
  source_url?: string;
  jurisdiction?: string;
}): Promise<ApiResult<{ item: SourceEvidence }>> {
  return callApi("POST", "/api/evidence", body);
}

// Persisted workflow runs (deterministic; no AI).
export function postPersistedRun(
  workflowKey: "packaging-audit" | "label-copy",
  body: Record<string, unknown>,
): Promise<ApiResult<{
  run_id: string;
  artifact_id: string;
  artifact_version: number;
  workflow_key: string;
  memory_items_created: number;
  draft_only: boolean;
  human_review_required: boolean;
  output: Record<string, unknown>;
}>> {
  return callApi("POST", `/api/workflows/${workflowKey}/run`, body);
}

// ---------------------------------------------------------------------------
// MM4 — internal AI gateway (status/prompts; ai_assist rides persisted runs)
// ---------------------------------------------------------------------------

export interface AIStatus {
  mode: "disabled" | "mock" | "live";
  enabled: boolean;
  provider: string | null;
  live_configured: boolean;
  live_missing_env: string | null;
  daily_cost_cap_usd: number;
  detail: string;
}

export function getAIStatus(): Promise<ApiResult<AIStatus>> {
  return callApi<AIStatus>("GET", "/api/ai/status");
}

export interface PromptVersionInfo {
  prompt_key: string;
  version: number;
  kind: string;
  task_type: string;
  workflow_key: string | null;
  content_hash: string;
  output_fields: string[];
}

export function getPrompts(): Promise<ApiResult<ItemsResponse<PromptVersionInfo>>> {
  return callApi("GET", "/api/prompts");
}

// ---------------------------------------------------------------------------
// MM5 — Export Center + service packs
// ---------------------------------------------------------------------------

export interface ExportMeta {
  id: string;
  artifact_id: string;
  version_id: string;
  format: "markdown" | "json" | "docx" | "pdf";
  kind: "draft" | "final";
  status: string;
  output_locale: string;
  rtl: boolean;
  file_name: string;
  content_type: string;
  content_size: number;
  sha256: string | null;
  review_status_at_export: string | null;
  artifact_status_at_export: string | null;
  source_gap_count: number;
  created_at: string;
}

export function postArtifactDecision(
  artifactId: string,
  decision: "review" | "approve" | "reject",
  note = "",
): Promise<ApiResult<{ artifact: Record<string, unknown> }>> {
  return callApi(
    "POST",
    `/api/artifacts/${encodeURIComponent(artifactId)}/${decision}`,
    { note },
  );
}

export function postArtifactExport(
  artifactId: string,
  body: { format: string; kind?: "draft" | "final"; version_id?: string },
): Promise<ApiResult<{ export: ExportMeta }>> {
  return callApi("POST", `/api/artifacts/${encodeURIComponent(artifactId)}/export`, body);
}

export function getExports(params?: {
  artifact_id?: string;
}): Promise<ApiResult<ItemsResponse<ExportMeta>>> {
  const qs = new URLSearchParams();
  if (params?.artifact_id) qs.set("artifact_id", params.artifact_id);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return callApi("GET", `/api/exports${suffix}`);
}

export function getExportMeta(id: string): Promise<ApiResult<{ export: ExportMeta }>> {
  return callApi("GET", `/api/exports/${encodeURIComponent(id)}`);
}

// Download URL for a generated export (browser navigates/fetches with
// the session cookie; the backend enforces workspace + permission).
export function exportDownloadUrl(id: string): string {
  return `${API_BASE}/api/exports/${encodeURIComponent(id)}/download`;
}

export interface ServicePackInfo {
  pack_id: string;
  name: string;
  positioning: string;
  target_customer: string;
  included_workflows: string[];
  intake_sections: string[];
  artifact_outputs: string[];
  export_formats: string[];
  human_review_required: boolean;
  risk_categories: string[];
  evidence_expectations: string;
  recommended_output_locales: string[];
  visibility: string;
  commercial_ready: boolean;
  not_yet_implemented: string[];
  disclaimer: string;
}

export function getServicePacks(): Promise<ApiResult<ItemsResponse<ServicePackInfo>>> {
  return callApi("GET", "/api/service-packs");
}

export function getServicePack(
  packId: string,
): Promise<ApiResult<{ pack: ServicePackInfo }>> {
  return callApi("GET", `/api/service-packs/${encodeURIComponent(packId)}`);
}

// ---------------------------------------------------------------------------
// MM6 — Knowledge Brain + evidence candidates + source-gap workflow
// ---------------------------------------------------------------------------

export interface KnowledgeFile {
  id: string;
  workspace_id: string;
  project_id: string | null;
  file_name: string;
  original_name: string;
  extension: string;
  mime_type: string;
  size_bytes: number;
  sha256: string;
  language_hint: string | null;
  status: string;
  duplicate_of: string | null;
  created_at: string;
}

export interface KnowledgeDocument {
  id: string;
  file_id: string;
  workspace_id: string;
  title: string;
  doc_type: string;
  language: string | null;
  language_method: string | null;
  chunk_count: number;
  created_at: string;
}

export interface KnowledgeChunk {
  id: string;
  document_id: string;
  chunk_index: number;
  section_label: string | null;
  content: string;
  char_count: number;
  language: string | null;
}

export interface KnowledgeSearchHit {
  chunk_id: string;
  document_id: string;
  document_title: string | null;
  section_label: string | null;
  language: string | null;
  content: string;
  score: number;
  matched_terms: string[];
}

export function uploadKnowledgeFile(
  file: File,
  opts?: { project_id?: string; language_hint?: string },
): Promise<ApiResult<{ file: KnowledgeFile }>> {
  const form = new FormData();
  form.append("upload", file);
  if (opts?.project_id) form.append("project_id", opts.project_id);
  if (opts?.language_hint) form.append("language_hint", opts.language_hint);
  return callApi("POST", "/api/knowledge/files", form);
}

export function getKnowledgeFiles(params?: {
  project_id?: string;
}): Promise<ApiResult<ItemsResponse<KnowledgeFile>>> {
  const qs = new URLSearchParams();
  if (params?.project_id) qs.set("project_id", params.project_id);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return callApi("GET", `/api/knowledge/files${suffix}`);
}

export function extractKnowledgeFile(
  fileId: string,
): Promise<ApiResult<{ extraction: unknown; document: KnowledgeDocument | null; file_status: string; warnings: string[] }>> {
  return callApi("POST", `/api/knowledge/files/${encodeURIComponent(fileId)}/extract`);
}

export function getKnowledgeDocuments(): Promise<ApiResult<ItemsResponse<KnowledgeDocument>>> {
  return callApi("GET", "/api/knowledge/documents");
}

export function getDocumentChunks(
  documentId: string,
): Promise<ApiResult<ItemsResponse<KnowledgeChunk>>> {
  return callApi(
    "GET",
    `/api/knowledge/documents/${encodeURIComponent(documentId)}/chunks`,
  );
}

export function searchKnowledge(
  q: string,
  limit = 8,
): Promise<ApiResult<ItemsResponse<KnowledgeSearchHit> & { retrieval: string }>> {
  const qs = new URLSearchParams({ q, limit: String(limit) });
  return callApi("GET", `/api/knowledge/search?${qs.toString()}`);
}

export function createEvidenceCandidate(body: {
  document_id: string;
  chunk_id?: string;
  source_type?: string;
  source_name: string;
  excerpt?: string;
  reliability?: string;
  jurisdiction?: string;
  project_id?: string;
}): Promise<ApiResult<{ item: Record<string, unknown> }>> {
  return callApi("POST", "/api/evidence/candidates", body);
}

export function rejectEvidence(
  evidenceId: string,
  note: string,
): Promise<ApiResult<{ decision: Record<string, unknown> }>> {
  return callApi(
    "POST",
    `/api/evidence/${encodeURIComponent(evidenceId)}/reject`,
    { note },
  );
}

export type SourceGapStatus =
  | "open"
  | "partially_supported"
  | "resolved"
  | "rejected"
  | "not_resolvable_currently";

export function getSourceGapDetail(
  gapId: string,
): Promise<ApiResult<Record<string, unknown>>> {
  return callApi("GET", `/api/source-gaps/${encodeURIComponent(gapId)}`);
}

export function linkGapEvidence(
  gapId: string,
  body: { evidence_id: string; relation?: "supports" | "partially_supports"; note?: string },
): Promise<ApiResult<{ link: Record<string, unknown> }>> {
  return callApi(
    "POST",
    `/api/source-gaps/${encodeURIComponent(gapId)}/link-evidence`,
    body,
  );
}

export function resolveSourceGap(
  gapId: string,
  body: { status: SourceGapStatus; explanation: string; evidence_id?: string },
): Promise<ApiResult<{ item: Record<string, unknown>; gap_status: string }>> {
  return callApi(
    "POST",
    `/api/source-gaps/${encodeURIComponent(gapId)}/resolve`,
    body,
  );
}

// ---------------------------------------------------------------------------
// MM7 — Admin console / commercial / public surface
// ---------------------------------------------------------------------------

export interface AdminOverview {
  cards: Record<string, number>;
  ai_readiness: { mode: string; enabled: boolean; live_gated: boolean };
  system: {
    commercial_ready: boolean;
    billing_enabled: boolean;
    payment_provider: string;
    phase: string;
  };
}

export function getAdminOverview(): Promise<ApiResult<AdminOverview>> {
  return callApi("GET", "/api/admin/overview");
}

export function getAdminUsers(): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  return callApi("GET", "/api/admin/users");
}

export function getAdminUserDetail(
  userId: string,
): Promise<ApiResult<Record<string, unknown>>> {
  return callApi("GET", `/api/admin/users/${encodeURIComponent(userId)}`);
}

export function getAdminWorkspaces(): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  return callApi("GET", "/api/admin/workspaces");
}

export function getAdminRoles(): Promise<ApiResult<Record<string, unknown>>> {
  return callApi("GET", "/api/admin/roles");
}

export function getAdminAuditEvents(): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  return callApi("GET", "/api/admin/audit-events");
}

export function getAdminUsage(): Promise<ApiResult<Record<string, unknown>>> {
  return callApi("GET", "/api/admin/usage");
}

export function getAdminJobs(): Promise<ApiResult<Record<string, unknown>>> {
  return callApi("GET", "/api/admin/jobs");
}

export function getAdminReviewQueue(): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  return callApi("GET", "/api/admin/review-queue");
}

export function getAdminEvidenceQueue(): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  return callApi("GET", "/api/admin/evidence-queue");
}

export function getAdminSourceGaps(): Promise<ApiResult<Record<string, unknown>>> {
  return callApi("GET", "/api/admin/source-gaps");
}

export function getAdminKnowledge(): Promise<ApiResult<Record<string, unknown>>> {
  return callApi("GET", "/api/admin/knowledge");
}

export function getAdminAiReadiness(): Promise<ApiResult<Record<string, unknown>>> {
  return callApi("GET", "/api/admin/ai-readiness");
}

export function getCommercialPlans(): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  return callApi("GET", "/api/commercial/plans");
}

export function getCommercialServicePacks(): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  return callApi("GET", "/api/commercial/service-packs");
}

export function updateServicePackProfile(
  packId: string,
  body: {
    commercial_ready?: boolean;
    pilot_ready?: boolean;
    pricing_status?: string;
    pricing_note?: string;
  },
): Promise<ApiResult<{ profile: Record<string, unknown> }>> {
  return callApi(
    "PATCH",
    `/api/commercial/service-packs/${encodeURIComponent(packId)}/profile`,
    body,
  );
}

export function getCommercialRequests(params?: {
  status?: string;
}): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return callApi("GET", `/api/commercial/requests${suffix}`);
}

export function updateCommercialRequestStatus(
  requestId: string,
  body: { status: string; note?: string },
): Promise<ApiResult<{ request: Record<string, unknown> }>> {
  return callApi(
    "POST",
    `/api/commercial/requests/${encodeURIComponent(requestId)}/status`,
    body,
  );
}

export function getSupportTickets(): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  return callApi("GET", "/api/support/tickets");
}

export function updateSupportTicketStatus(
  ticketId: string,
  body: { status: string; note?: string },
): Promise<ApiResult<{ ticket: Record<string, unknown> }>> {
  return callApi(
    "POST",
    `/api/support/tickets/${encodeURIComponent(ticketId)}/status`,
    body,
  );
}

// ---- Public (unauthenticated) surface ----

export interface PublicServicePack {
  pack_id: string;
  name: string;
  positioning: string;
  target_customer: string;
  included_workflows: string[];
  artifact_outputs: string[];
  export_formats: string[];
  human_review_required: boolean;
  recommended_output_locales: string[];
  known_gaps: string[];
  commercial_ready: boolean;
  pilot_ready: boolean;
  pricing_status: string;
  pricing: string;
  disclaimer: string;
}

export function getPublicServicePacks(): Promise<
  ApiResult<ItemsResponse<PublicServicePack> & { billing_enabled: boolean }>
> {
  return callApi("GET", "/api/public/service-packs");
}

export function getPublicPlans(): Promise<ApiResult<ItemsResponse<Record<string, unknown>>>> {
  return callApi("GET", "/api/public/plans");
}

export interface PublicRequestPayload {
  request_type: string;
  requester_name: string;
  email: string;
  company?: string;
  country?: string;
  preferred_language?: string;
  service_pack_id?: string;
  product_category?: string;
  message?: string;
  source_page?: string;
}

export function postPublicRequest(
  body: PublicRequestPayload,
): Promise<ApiResult<{ ok: boolean; stored: boolean; request_id?: string; email_sent: boolean }>> {
  return callApi("POST", "/api/public/requests", body);
}

export function postPublicFeedback(body: {
  category: string;
  subject: string;
  message: string;
  contact_email?: string;
}): Promise<ApiResult<{ ok: boolean; stored: boolean; ticket_id?: string }>> {
  return callApi("POST", "/api/public/feedback", body);
}

// ---------------------------------------------------------------------------
// Final closure — persisted-first workflow runner.
//
// Authorized users get REAL persisted runs (run + artifact + memory);
// on permission_denied the call falls back to the legacy dry-run so
// preview/limited users see exactly the pre-existing behavior. The
// engine payload shape is identical; persisted runs add `persisted_run`
// metadata which the shared DataState banner surfaces.
// ---------------------------------------------------------------------------

export interface PersistedRunInfo {
  run_id: string;
  artifact_id: string;
  human_review_required: boolean;
}

export async function runWorkflowPersistedFirst<TReq, TRes>(
  workflowKey: string,
  body: TReq,
  dryRunner: (b: TReq) => Promise<ApiResult<TRes>>,
): Promise<ApiResult<TRes & { persisted_run?: PersistedRunInfo }>> {
  const persisted = await callApi<{
    run_id: string;
    artifact_id: string;
    human_review_required: boolean;
    output: TRes;
  }>("POST", `/api/workflows/${encodeURIComponent(workflowKey)}/run`, body);
  if (
    persisted.outcome === "ok" &&
    persisted.data &&
    typeof persisted.data.output === "object" &&
    persisted.data.run_id
  ) {
    const merged = {
      ...(persisted.data.output as TRes),
      persisted_run: {
        run_id: persisted.data.run_id,
        artifact_id: persisted.data.artifact_id,
        human_review_required: persisted.data.human_review_required,
      },
    };
    return { ...persisted, data: merged } as ApiResult<
      TRes & { persisted_run?: PersistedRunInfo }
    >;
  }
  if (persisted.status === 403 || persisted.status === 401) {
    return (await dryRunner(body)) as ApiResult<
      TRes & { persisted_run?: PersistedRunInfo }
    >;
  }
  return persisted as unknown as ApiResult<
    TRes & { persisted_run?: PersistedRunInfo }
  >;
}

// ---------------------------------------------------------------------------
// Phase 2 MM9 — internal team + prospect/project studio
// ---------------------------------------------------------------------------

type Row = Record<string, unknown>;

export const getTeamMembers = () =>
  callApi<ItemsResponse<Row>>("GET", "/api/team/members");
export const addTeamMember = (b: Row) =>
  callApi<{ item: Row }>("POST", "/api/team/members", b);
export const patchTeamMember = (id: string, b: Row) =>
  callApi<{ item: Row }>("PATCH", `/api/team/members/${encodeURIComponent(id)}`, b);
export const suspendTeamMember = (id: string) =>
  callApi<{ item: Row }>("POST", `/api/team/members/${encodeURIComponent(id)}/suspend`);
export const reactivateTeamMember = (id: string) =>
  callApi<{ item: Row }>("POST", `/api/team/members/${encodeURIComponent(id)}/reactivate`);
export const getTeamAuthReadiness = () =>
  callApi<Row>("GET", "/api/team/auth-readiness");
export const getGoogleAuthStatus = () =>
  callApi<{ enabled: boolean; detail: string }>("GET", "/api/auth/google/status");

export const getProspects = (params?: Record<string, string>) => {
  const qs = new URLSearchParams(params ?? {}).toString();
  return callApi<ItemsResponse<Row>>("GET", `/api/prospects${qs ? `?${qs}` : ""}`);
};
export const createProspect = (b: Row) =>
  callApi<{ item: Row }>("POST", "/api/prospects", b);
export const getProspect = (id: string) =>
  callApi<{ item: Row; projects: Row[] }>("GET", `/api/prospects/${encodeURIComponent(id)}`);
export const patchProspect = (id: string, b: Row) =>
  callApi<{ item: Row }>("PATCH", `/api/prospects/${encodeURIComponent(id)}`, b);
export const archiveProspect = (id: string) =>
  callApi<{ item: Row }>("POST", `/api/prospects/${encodeURIComponent(id)}/archive`);

export const getStudioProjects = (params?: Record<string, string>) => {
  const qs = new URLSearchParams(params ?? {}).toString();
  return callApi<ItemsResponse<Row>>("GET", `/api/projects${qs ? `?${qs}` : ""}`);
};
export const createStudioProject = (b: Row) =>
  callApi<{ item: Row }>("POST", "/api/projects", b);
export const getStudioProject = (id: string) =>
  callApi<Row>("GET", `/api/projects/${encodeURIComponent(id)}`);
export const setStudioProjectStatus = (id: string, status: string) =>
  callApi<{ item: Row }>("POST", `/api/projects/${encodeURIComponent(id)}/status`, { status });
export const getStudioBrief = (id: string) =>
  callApi<{ item: Row | null }>("GET", `/api/projects/${encodeURIComponent(id)}/brief`);
export const putStudioBrief = (id: string, brief: Row) =>
  callApi<{ completeness: number; missing_fields: string[] }>(
    "PUT", `/api/projects/${encodeURIComponent(id)}/brief`, { brief });
export const getStudioReferences = (id: string) =>
  callApi<ItemsResponse<Row>>("GET", `/api/projects/${encodeURIComponent(id)}/references`);
export function uploadStudioReference(id: string, file: File, referenceType: string, notes?: string) {
  const form = new FormData();
  form.append("upload", file);
  form.append("reference_type", referenceType);
  if (notes) form.append("notes", notes);
  return callApi<{ item: Row; warnings: string[] }>(
    "POST", `/api/projects/${encodeURIComponent(id)}/references`, form);
}
export const getGenerationBriefs = (id: string) =>
  callApi<ItemsResponse<Row>>("GET", `/api/projects/${encodeURIComponent(id)}/generation-briefs`);
export const createGenerationBrief = (id: string, notes?: string) =>
  callApi<{ item: Row; compiled: Row; artifact_id: string | null }>(
    "POST", `/api/projects/${encodeURIComponent(id)}/generation-briefs`,
    { review_notes: notes ?? null });
export const getStudioActivity = (id: string) =>
  callApi<ItemsResponse<Row>>("GET", `/api/projects/${encodeURIComponent(id)}/activity`);

// Phase 2 MM10 — visual concept studio (image/mockup generation).
export const getImageStatus = () =>
  callApi<{ status: Row; honesty: Row }>("GET", "/api/studio/image/status");
export const getVisualConceptSets = (projectId: string) =>
  callApi<ItemsResponse<Row>>(
    "GET", `/api/projects/${encodeURIComponent(projectId)}/visual-concept-sets`);
export const createVisualConceptSet = (projectId: string, b: Row) =>
  callApi<{ item: Row; prompt_preview: Row; image_generated: boolean }>(
    "POST", `/api/projects/${encodeURIComponent(projectId)}/visual-concept-sets`, b);
export const generateVisualConceptSet = (projectId: string, setId: string) =>
  callApi<{ job: Row; concept_set: Row; assets: Row[]; is_mock: boolean }>(
    "POST", `/api/projects/${encodeURIComponent(projectId)}/visual-concept-sets/${encodeURIComponent(setId)}/generate`);
export const archiveVisualConceptSet = (projectId: string, setId: string) =>
  callApi<{ item: Row }>(
    "POST", `/api/projects/${encodeURIComponent(projectId)}/visual-concept-sets/${encodeURIComponent(setId)}/archive`);
export const getVisualAssets = (projectId: string, setId?: string) =>
  callApi<ItemsResponse<Row>>(
    "GET", `/api/projects/${encodeURIComponent(projectId)}/visual-assets${setId ? `?set_id=${encodeURIComponent(setId)}` : ""}`);
export const selectVisualAsset = (projectId: string, assetId: string, notes?: string, rank?: number) =>
  callApi<{ item: Row }>(
    "POST", `/api/projects/${encodeURIComponent(projectId)}/visual-assets/${encodeURIComponent(assetId)}/select`,
    { notes: notes ?? "", selection_rank: rank ?? null });
export const rejectVisualAsset = (projectId: string, assetId: string, notes?: string) =>
  callApi<{ item: Row }>(
    "POST", `/api/projects/${encodeURIComponent(projectId)}/visual-assets/${encodeURIComponent(assetId)}/reject`,
    { notes: notes ?? "" });
export const exportVisualPreviewPack = (projectId: string, setId: string) =>
  callApi<{ artifact_id: string | null; pack: Row }>(
    "POST", `/api/projects/${encodeURIComponent(projectId)}/visual-concept-sets/${encodeURIComponent(setId)}/export-preview-pack`);
export const visualAssetPreviewUrl = (projectId: string, assetId: string) =>
  `/api/projects/${encodeURIComponent(projectId)}/visual-assets/${encodeURIComponent(assetId)}/preview`;

// Phase 2 MM11 — artwork assembly + print handoff.
const _awBase = (projectId: string) =>
  `/api/projects/${encodeURIComponent(projectId)}/artwork-packages`;
export const getArtworkTemplates = () =>
  callApi<ItemsResponse<Row>>("GET", "/api/artwork/templates");
export const getArtworkPackages = (projectId: string) =>
  callApi<ItemsResponse<Row>>("GET", _awBase(projectId));
export const createArtworkPackage = (projectId: string, b: Row) =>
  callApi<{ item: Row; honesty: Row }>("POST", _awBase(projectId), b);
export const getArtworkPackage = (projectId: string, pkgId: string) =>
  callApi<{ item: Row }>("GET", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}`);
export const setArtworkStatus = (projectId: string, pkgId: string, status: string) =>
  callApi<{ item: Row }>("POST", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/status`, { status });
export const getArtworkPanels = (projectId: string, pkgId: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/panels`);
export const getArtworkCopyBlocks = (projectId: string, pkgId: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/copy-blocks`);
export const patchArtworkCopy = (projectId: string, pkgId: string, copyId: string, b: Row) =>
  callApi<{ item: Row }>("PATCH", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/copy-blocks/${encodeURIComponent(copyId)}`, b);
export const reviewArtworkCopy = (projectId: string, pkgId: string, copyId: string, decision: string) =>
  callApi<{ item: Row }>("POST", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/copy-blocks/${encodeURIComponent(copyId)}/review`, { review_status: decision });
export const getArtworkDielines = (projectId: string, pkgId: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/dielines`);
export function uploadArtworkDieline(projectId: string, pkgId: string, file: File) {
  const form = new FormData();
  form.append("upload", file);
  return callApi<{ item: Row }>("POST", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/dielines`, form);
}
export const getArtworkChecklist = (projectId: string, pkgId: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/checklist`);
export const patchArtworkChecklist = (projectId: string, pkgId: string, itemId: string, status: string) =>
  callApi<{ item: Row }>("PATCH", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/checklist/${encodeURIComponent(itemId)}`, { status });
export const createArtworkExport = (projectId: string, pkgId: string, exportType: string) =>
  callApi<{ export: Row; artifact_id: string | null }>("POST", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/exports`, { export_type: exportType });
export const getArtworkExports = (projectId: string, pkgId: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/exports`);
export const artworkExportDownloadUrl = (projectId: string, pkgId: string, exportId: string) =>
  `${_awBase(projectId)}/${encodeURIComponent(pkgId)}/exports/${encodeURIComponent(exportId)}/download`;

// Phase 2 MM12 — compliance / regulatory / human approval.
const _cmBase = (projectId: string) =>
  `/api/projects/${encodeURIComponent(projectId)}/compliance-cases`;
export const getComplianceCases = (projectId: string) =>
  callApi<ItemsResponse<Row>>("GET", _cmBase(projectId));
export const createComplianceCase = (projectId: string, b: Row) =>
  callApi<{ item: Row; honesty: Row }>("POST", _cmBase(projectId), b);
export const getComplianceCase = (projectId: string, caseId: string) =>
  callApi<{ item: Row }>("GET", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}`);
export const getComplianceChecklist = (projectId: string, caseId: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/checklist`);
export const patchComplianceChecklist = (projectId: string, caseId: string, itemId: string, status: string, notes?: string) =>
  callApi<{ item: Row }>("PATCH", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/checklist/${encodeURIComponent(itemId)}`, { status, notes: notes ?? "" });
export const getComplianceClaims = (projectId: string, caseId: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/claims`);
export const patchComplianceClaim = (projectId: string, caseId: string, claimId: string, b: Row) =>
  callApi<{ item: Row }>("PATCH", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/claims/${encodeURIComponent(claimId)}`, b);
export const getComplianceLanguage = (projectId: string, caseId: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/language`);
export const patchComplianceLanguage = (projectId: string, caseId: string, itemId: string, status: string) =>
  callApi<{ item: Row }>("PATCH", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/language/${encodeURIComponent(itemId)}`, { status });
export const getComplianceSignoffs = (projectId: string, caseId: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/signoffs`);
export const requestComplianceSignoff = (projectId: string, caseId: string, approvalType: string) =>
  callApi<{ item: Row }>("POST", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/signoffs`, { approval_type: approvalType });
export const decideComplianceSignoff = (projectId: string, caseId: string, signoffId: string, status: string, notes: string) =>
  callApi<{ item: Row }>("POST", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/signoffs/${encodeURIComponent(signoffId)}/decision`, { status, notes });
export const getComplianceGate = (projectId: string, caseId: string) =>
  callApi<{ gate: string; reasons: string[]; missing_items: string[] }>("GET", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/gate`);
export const evaluateComplianceGate = (projectId: string, caseId: string, requestedGate: string) =>
  callApi<{ item: Row; gate: string; reasons: string[] }>("POST", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/gate/evaluate`, { requested_gate: requestedGate });
export const createComplianceExport = (projectId: string, caseId: string, exportType: string) =>
  callApi<{ artifact_id: string | null }>("POST", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/exports`, { export_type: exportType });
export const getComplianceExports = (projectId: string, caseId: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_cmBase(projectId)}/${encodeURIComponent(caseId)}/exports`);

// Phase 2 MM13 — supplier directory / RFQ / quote comparison.
export const getSupplierStatus = () =>
  callApi<{ supplier_directory: string; honesty: Row }>("GET", "/api/suppliers/status");
export const getSuppliers = (search?: string) =>
  callApi<ItemsResponse<Row>>("GET", `/api/suppliers${search ? `?search=${encodeURIComponent(search)}` : ""}`);
export const createSupplier = (b: Row) =>
  callApi<{ item: Row }>("POST", "/api/suppliers", b);
export const getSupplier = (id: string) =>
  callApi<{ item: Row; certifications: Row[] }>("GET", `/api/suppliers/${encodeURIComponent(id)}`);
export const patchSupplier = (id: string, b: Row) =>
  callApi<{ item: Row }>("PATCH", `/api/suppliers/${encodeURIComponent(id)}`, b);
export const archiveSupplier = (id: string) =>
  callApi<{ item: Row }>("POST", `/api/suppliers/${encodeURIComponent(id)}/archive`);
const _rfqBase = (pid: string) => `/api/projects/${encodeURIComponent(pid)}/rfqs`;
export const getRfqs = (pid: string) =>
  callApi<ItemsResponse<Row>>("GET", _rfqBase(pid));
export const createRfq = (pid: string, b: Row) =>
  callApi<{ item: Row }>("POST", _rfqBase(pid), b);
export const getRfq = (pid: string, rid: string) =>
  callApi<{ item: Row }>("GET", `${_rfqBase(pid)}/${encodeURIComponent(rid)}`);
export const patchRfq = (pid: string, rid: string, b: Row) =>
  callApi<{ item: Row }>("PATCH", `${_rfqBase(pid)}/${encodeURIComponent(rid)}`, b);
export const generateShortlist = (pid: string, rid: string) =>
  callApi<ItemsResponse<Row>>("POST", `${_rfqBase(pid)}/${encodeURIComponent(rid)}/shortlist`);
export const getRfqSuppliers = (pid: string, rid: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_rfqBase(pid)}/${encodeURIComponent(rid)}/suppliers`);
export const getRfqQuotes = (pid: string, rid: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_rfqBase(pid)}/${encodeURIComponent(rid)}/quotes`);
export const createQuote = (pid: string, rid: string, b: Row) =>
  callApi<{ item: Row }>("POST", `${_rfqBase(pid)}/${encodeURIComponent(rid)}/quotes`, b);
export const addQuoteTier = (pid: string, rid: string, qid: string, b: Row) =>
  callApi<{ item: Row }>("POST", `${_rfqBase(pid)}/${encodeURIComponent(rid)}/quotes/${encodeURIComponent(qid)}/tiers`, b);
export const buildComparison = (pid: string, rid: string) =>
  callApi<{ comparison: Row; matrix: Row }>("POST", `${_rfqBase(pid)}/${encodeURIComponent(rid)}/comparison`);
export const draftRecommendation = (pid: string, rid: string, cmpId: string) =>
  callApi<{ decision: Row; recommendation: Row }>("POST", `${_rfqBase(pid)}/${encodeURIComponent(rid)}/comparisons/${encodeURIComponent(cmpId)}/recommend`);
export const logSupplierDecision = (pid: string, rid: string, decId: string, decision: string, note: string) =>
  callApi<{ item: Row }>("POST", `${_rfqBase(pid)}/${encodeURIComponent(rid)}/decisions/${encodeURIComponent(decId)}/log`, { human_decision: decision, note });
export const exportRfqPack = (pid: string, rid: string) =>
  callApi<{ artifact_id: string | null }>("POST", `${_rfqBase(pid)}/${encodeURIComponent(rid)}/export-rfq-pack`);

// Phase 2 MM14 — professional sales package builder.
const _spBase = (pid: string) => `/api/projects/${encodeURIComponent(pid)}/sales-packages`;
export const getSalesStatus = () =>
  callApi<{ sales_package_builder: string; honesty: Row }>("GET", "/api/sales-packages/status");
export const getSalesPackages = (pid: string) =>
  callApi<ItemsResponse<Row>>("GET", _spBase(pid));
export const createSalesPackage = (pid: string, b: Row) =>
  callApi<{ item: Row; honesty: Row }>("POST", _spBase(pid), b);
export const getSalesPackage = (pid: string, id: string) =>
  callApi<{ item: Row }>("GET", `${_spBase(pid)}/${encodeURIComponent(id)}`);
export const getSalesSections = (pid: string, id: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_spBase(pid)}/${encodeURIComponent(id)}/sections`);
export const patchSalesSection = (pid: string, id: string, secId: string, b: Row) =>
  callApi<{ item: Row }>("PATCH", `${_spBase(pid)}/${encodeURIComponent(id)}/sections/${encodeURIComponent(secId)}`, b);
export const getSalesPricing = (pid: string, id: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_spBase(pid)}/${encodeURIComponent(id)}/pricing`);
export const addSalesPricing = (pid: string, id: string, b: Row) =>
  callApi<{ item: Row }>("POST", `${_spBase(pid)}/${encodeURIComponent(id)}/pricing`, b);
export const getSalesGate = (pid: string, id: string) =>
  callApi<{ gate: string; blocking_reasons: string[]; warnings: string[]; client_preview_allowed: boolean }>("GET", `${_spBase(pid)}/${encodeURIComponent(id)}/gate`);
export const addSalesDecision = (pid: string, id: string, decision: string, note: string) =>
  callApi<{ item: Row }>("POST", `${_spBase(pid)}/${encodeURIComponent(id)}/decisions`, { decision, note });
export const exportSalesInternal = (pid: string, id: string) =>
  callApi<{ artifact_id: string | null }>("POST", `${_spBase(pid)}/${encodeURIComponent(id)}/export-internal`, { format: "markdown" });
export const exportSalesClientPreview = (pid: string, id: string) =>
  callApi<{ artifact_id: string | null; watermarked: boolean }>("POST", `${_spBase(pid)}/${encodeURIComponent(id)}/export-client-preview`, { format: "markdown" });
export const exportSalesCaseStudy = (pid: string, id: string) =>
  callApi<{ artifact_id: string | null }>("POST", `${_spBase(pid)}/${encodeURIComponent(id)}/export-case-study`, { format: "markdown" });

// Phase 2 MM15 — internal production workflow / QA.
const _pdBase = (pid: string) => `/api/projects/${encodeURIComponent(pid)}/production`;
export const getProductionStatus = () =>
  callApi<{ production_workflow: string; honesty: Row }>("GET", "/api/production/status");
export const getRealTeamTestPlan = () =>
  callApi<{ plan: Row }>("GET", "/api/production/real-team-test-plan");
export const getProductionWorkflow = (pid: string) =>
  callApi<{ item: Row | null; exists: boolean }>("GET", _pdBase(pid));
export const createProductionWorkflow = (pid: string) =>
  callApi<{ item: Row }>("POST", _pdBase(pid));
export const recalcProduction = (pid: string, wf: string) =>
  callApi<{ item: Row }>("POST", `${_pdBase(pid)}/${encodeURIComponent(wf)}/recalculate`);
export const getProductionChecklist = (pid: string, wf: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_pdBase(pid)}/${encodeURIComponent(wf)}/checklist`);
export const patchProductionChecklist = (pid: string, wf: string, itemId: string, status: string, note: string) =>
  callApi<{ item: Row }>("PATCH", `${_pdBase(pid)}/${encodeURIComponent(wf)}/checklist/${encodeURIComponent(itemId)}`, { status, note });
export const getProductionAssignments = (pid: string, wf: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_pdBase(pid)}/${encodeURIComponent(wf)}/assignments`);
export const getProductionRevisions = (pid: string, wf: string) =>
  callApi<ItemsResponse<Row>>("GET", `${_pdBase(pid)}/${encodeURIComponent(wf)}/revisions`);
export const createProductionRevision = (pid: string, wf: string, b: Row) =>
  callApi<{ item: Row }>("POST", `${_pdBase(pid)}/${encodeURIComponent(wf)}/revisions`, b);
export const resolveProductionRevision = (pid: string, wf: string, revId: string, note: string) =>
  callApi<{ item: Row }>("PATCH", `${_pdBase(pid)}/${encodeURIComponent(wf)}/revisions/${encodeURIComponent(revId)}`, { status: "resolved", resolution_note: note });
export const getFinalDelivery = (pid: string, wf: string) =>
  callApi<{ item: Row | null }>("GET", `${_pdBase(pid)}/${encodeURIComponent(wf)}/final-delivery`);
export const patchFinalDelivery = (pid: string, wf: string, b: Row) =>
  callApi<{ item: Row }>("PATCH", `${_pdBase(pid)}/${encodeURIComponent(wf)}/final-delivery`, b);
export const getProductionGate = (pid: string, wf: string) =>
  callApi<{ qa_status: string; readiness_score: number; blocking_reasons: string[]; warnings: string[]; owner_test_ready: boolean }>("GET", `${_pdBase(pid)}/${encodeURIComponent(wf)}/gate`);
export const addProductionDecision = (pid: string, wf: string, decision: string, note: string) =>
  callApi<{ item: Row }>("POST", `${_pdBase(pid)}/${encodeURIComponent(wf)}/decisions`, { decision, note });
export const exportProductionQaReport = (pid: string, wf: string) =>
  callApi<{ artifact_id: string | null }>("POST", `${_pdBase(pid)}/${encodeURIComponent(wf)}/export-qa-report`, { format: "markdown" });
export const exportOwnerTestPack = (pid: string, wf: string) =>
  callApi<{ artifact_id: string | null }>("POST", `${_pdBase(pid)}/${encodeURIComponent(wf)}/export-owner-test-pack`, { format: "markdown" });

// Internal export for tests that override the fetch transport.
export const __test__ = { callApi };
